/**
 * ReportList Component
 *
 * Displays a list of available reports for the selected department.
 * Shows reports as cards, with unavailable reports grayed out.
 */

import React from 'react';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition, ReportDepartmentKey, isReportAvailableForLocation } from '../../types/reports';
import { getReportsByDepartment } from '../../config/reportDefinitions';
import { ReportCard } from './ReportCard';
import { getReportDepartmentLabel } from './ReportDepartmentTabs';
import { useAuth } from '../../auth/AuthContext';

interface ReportListProps {
  department: ReportDepartmentKey;
  locationId: number;
  locationName: string;
  onReportSelect: (report: ReportDefinition) => void;
}

export const ReportList: React.FC<ReportListProps> = ({
  department,
  locationId,
  locationName,
  onReportSelect,
}) => {
  const { userRole } = useAuth();
  const reports = getReportsByDepartment(department);
  const departmentLabel = getReportDepartmentLabel(department);

  // Separate available and unavailable reports
  const { available, unavailable } = reports.reduce(
    (acc, report) => {
      if (isReportAvailableForLocation(report, locationId, userRole)) {
        acc.available.push(report);
      } else {
        acc.unavailable.push(report);
      }
      return acc;
    },
    { available: [] as ReportDefinition[], unavailable: [] as ReportDefinition[] }
  );

  // Show available reports first, then unavailable
  const sortedReports = [...available, ...unavailable];

  if (reports.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center py-8">
          <DocumentChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">
            No reports configured for {departmentLabel}.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Reports will be added in future updates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700">
          Available Reports for {departmentLabel}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {available.length} of {reports.length} reports available at {locationName}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sortedReports.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            isAvailable={isReportAvailableForLocation(report, locationId, userRole)}
            isComingSoon={!!report.comingSoon || (locationId === 0 && !report.consolidatedOnly && !report.allowsConsolidated)}
            locationName={locationName}
            onClick={() => onReportSelect(report)}
          />
        ))}
      </div>
    </div>
  );
};
